import { createDataMapper, mergeWithDefault } from '@common/utils';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import {
    BackendCollectionMetadata,
    CollectionMetadataResponse,
    Credits,
    DEFAULT_CREDITS,
    DEFAULT_IMAGE,
    DEFAULT_MULTILINGUAL_METADATA,
    GetCollectionMetadataArgs,
    Image,
    MultilingualCollectionMetadata,
    LocalizedSynopses,
    MamutilsBaseSchema,
} from './metadataTypes';

const mergeSources = (
    sources?: BackendCollectionMetadata['sources'],
): Partial<MamutilsBaseSchema> => {
    if (!sources) return {};

    const priorityOrder = [
        sources.viva,
        sources.tmdb,
        sources.proscope,
        sources.user_overrides,
    ].filter(Boolean) as MamutilsBaseSchema[];

    return priorityOrder.reduce(
        (targetFallback, currentSource) =>
            mergeWithDefault(currentSource, targetFallback),
        {},
    );
};

const formatImage = (image?: Image | null) =>
    mergeWithDefault(image, DEFAULT_IMAGE);

const formatCredits = (credits?: Credits): Credits =>
    mergeWithDefault(credits, DEFAULT_CREDITS);

const mapBackendCollectionMetadataToFrontendCollectionMetadata = (
    backendMetadata: BackendCollectionMetadata,
    _meta: FetchBaseQueryMeta | undefined,
    arg: GetCollectionMetadataArgs,
): CollectionMetadataResponse => {
    const { proscopeId, collectionId } = arg;

    const mergedSources = mergeSources(backendMetadata.sources);

    const rawResult: Partial<MultilingualCollectionMetadata> = {
        proscopeId,
        collectionId,
        title: mergedSources.title,
        duration: mergedSources.duration,
        releaseDate: mergedSources.releaseYear?.toString(),
        productionCountry: mergedSources.country,
        genres: mergedSources.genres,
        credits: formatCredits(mergedSources.credits),
        synopses: mergedSources.synopsis as LocalizedSynopses,
        images: {
            background: formatImage(mergedSources.images?.background),
            cover: formatImage(mergedSources.images?.cover),
            landscape: formatImage(mergedSources.images?.landscape),
        },
        trailers: mergedSources.trailers || [],
    };

    return {
        merged: mergeWithDefault(rawResult, DEFAULT_MULTILINGUAL_METADATA),
        sources: backendMetadata.sources || {},
    };
};

export const transformCollectionMetadata = createDataMapper<
    BackendCollectionMetadata,
    CollectionMetadataResponse,
    GetCollectionMetadataArgs
>(mapBackendCollectionMetadataToFrontendCollectionMetadata, false);
